import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AuthAPI } from "../api";
import MentorRequestModal from "../components/MentorRequestModal";
import ConfirmModal from "../components/ConfirmModal";
import DateTimePicker from "../components/DateTimePicker";

const MentorDashboard = () => {
  const [slots, setSlots] = useState([]);
  const [newSlot, setNewSlot] = useState({ start_time: null, end_time: null });
  const [slotError, setSlotError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadWarning, setLoadWarning] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState(null);

  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  const [sessions, setSessions] = useState([]);

  const [toast, setToast] = useState(null);
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchSlots = useCallback(async () => {
    try {
      const res = await AuthAPI.get("availability/");
      // The backend already scopes availability/ to the logged-in mentor's slots,
      // so no client-side filtering by userId is needed (and parseInt(null) === NaN
      // would silently empty the list if userId was missing from localStorage).
      setSlots(res.data);
    } catch (err) {
      console.error("Fetch slots error:", err);
      throw err;
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await AuthAPI.get("session-requests/");
      setRequests(res.data);
    } catch (err) {
      console.error("Fetch requests error:", err);
      throw err;
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await AuthAPI.get("sessions/");
      setSessions(res.data);
    } catch (err) {
      console.error("Error fetching sessions", err);
      throw err;
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoadWarning(false);
    setLoading(true);
    const results = await Promise.allSettled([fetchSlots(), fetchRequests(), fetchSessions()]);
    setLoadWarning(results.some((result) => result.status === "rejected"));
    setLoading(false);
  }, [fetchRequests, fetchSessions, fetchSlots]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleAdd = async () => {
    if (!newSlot.start_time || !newSlot.end_time) {
      setSlotError("Please select both a start and end time.");
      return;
    }
    const start = newSlot.start_time;
    const end = newSlot.end_time;
    if (end <= start) {
      setSlotError("End time must be after start time.");
      return;
    }
    setSlotError("");
    try {
      await AuthAPI.post("availability/", {
        start_time: start.toISOString(),
        end_time: end.toISOString(),
      });
      setNewSlot({ start_time: null, end_time: null });
      await loadDashboard();
      showToast("Availability added.");
    } catch (err) {
      console.error("Add slot error:", err);
      showToast("Failed to add slot. Please try again.", "error");
    }
  };

  const handleDelete = async (id) => {
    try {
      await AuthAPI.delete(`availability/${id}/`);
      await loadDashboard();
      showToast("Availability removed.");
    } catch (err) {
      console.error("Delete slot error:", err);
      showToast("Failed to remove slot. Please try again.", "error");
    }
  };

  const handleRequestAction = async (action, payload) => {
    try {
      if (action === "approve") {
        await AuthAPI.patch(`session-requests/${selectedRequest.id}/approve/`);
      }
      if (action === "reject") {
        await AuthAPI.patch(`session-requests/${selectedRequest.id}/reject/`, payload);
      }
      if (action === "counter") {
        await AuthAPI.patch(`session-requests/${selectedRequest.id}/counter/`, payload);
      }
      setRequestModalOpen(false);
      setSelectedRequest(null);
      await loadDashboard();
      showToast("Request updated.");
    } catch (err) {
      console.error("Request action error:", err);
      showToast("Action failed. Please try again.", "error");
    }
  };

  const upcomingSessions = sessions.filter((s) => s.status === "booked");
  const pendingRequests = requests.filter((r) => r.status === "pending");
  const completedSessions = sessions.filter((s) => s.status === "completed");

  const formatDateTime = (iso) =>
    new Date(iso).toLocaleString([], {
      weekday: "short", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <svg className="h-8 w-8 animate-spin text-[#282E6A]" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 sm:px-6">
      {/* Modals */}
      <MentorRequestModal
        isOpen={requestModalOpen}
        request={selectedRequest}
        onClose={() => setRequestModalOpen(false)}
        onAction={handleRequestAction}
      />

      <ConfirmModal
        isOpen={confirmOpen}
        title="Remove availability slot"
        message="Are you sure you want to remove this slot?"
        onConfirm={() => {
          handleDelete(selectedSlotId);
          setConfirmOpen(false);
          setSelectedSlotId(null);
        }}
        onCancel={() => {
          setConfirmOpen(false);
          setSelectedSlotId(null);
        }}
      />

      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium ${
          toast.type === "success"
            ? "bg-green-50 text-green-800 border border-green-200"
            : "bg-red-50 text-red-800 border border-red-200"
        }`}>
          {toast.type === "success" ? (
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          {toast.message}
        </div>
      )}

      {loadWarning && (
        <div className="flex items-start justify-between gap-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div>
            <p className="font-medium">Some dashboard data could not be refreshed.</p>
            <p className="mt-1 text-amber-800">
              Availability, requests, and sessions load independently, so the sections below may be partially updated.
            </p>
          </div>
          <button
            onClick={loadDashboard}
            className="flex-shrink-0 rounded-lg border border-amber-300 px-3 py-1.5 font-medium text-amber-900 transition hover:bg-amber-100"
          >
            Retry
          </button>
        </div>
      )}

      <section className="overflow-hidden rounded-[30px] border border-[#dfe4f2] bg-white/85 shadow-[0_28px_70px_-48px_rgba(23,30,67,0.45)] backdrop-blur-sm">
        <div className="grid gap-6 px-6 py-7 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
          <div>
            <span className="inline-flex rounded-full border border-[#cfd5ee] bg-[#eef0fa] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#282E6A]">
              Mentor workspace
            </span>
            <h1 className="mt-4 text-3xl leading-tight text-[#111425] sm:text-4xl">
              Manage your requests, live sessions, and availability without losing context.
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-[#5d647f] sm:text-base">
              This is the operational view for mentoring: confirm incoming requests, protect time on your calendar, and keep sessions moving cleanly.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/calendar"
                className="inline-flex items-center justify-center rounded-xl bg-[#282E6A] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#1e2458]"
              >
                Open calendar
              </Link>
              <Link
                to="/mybookings"
                className="inline-flex items-center justify-center rounded-xl border border-[#cfd5ee] bg-white px-5 py-2.5 text-sm font-medium text-[#282E6A] transition hover:bg-[#eef0fa]"
              >
                View session history
              </Link>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
            {[
              { label: "Pending requests", value: pendingRequests.length, tone: "bg-[#111425] text-white border-[#202640]" },
              { label: "Upcoming sessions", value: upcomingSessions.length, tone: "bg-[#eef0fa] text-[#111425] border-[#d9def3]" },
              { label: "Open slots", value: slots.length, tone: "bg-[#f5f1e7] text-[#111425] border-[#ead9b7]" },
            ].map((stat) => (
              <div key={stat.label} className={`rounded-[22px] border p-4 ${stat.tone}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-70">{stat.label}</p>
                <p className="mt-3 text-3xl font-semibold">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= Upcoming Sessions ================= */}
      <section className="rounded-[28px] border border-[#dfe4f2] bg-white/92 p-6 shadow-[0_22px_60px_-44px_rgba(23,30,67,0.35)]">
        <h2 className="text-xl font-semibold text-[#111425]">
          Upcoming Sessions
          {upcomingSessions.length > 0 && (
            <span className="ml-2 rounded-full bg-[#eef0fa] px-2.5 py-0.5 text-sm font-normal text-[#282E6A]">
              {upcomingSessions.length}
            </span>
          )}
        </h2>

        {upcomingSessions.length === 0 ? (
          <div className="mt-5 rounded-[22px] border border-dashed border-[#d4daec] bg-[#f7f8fc] px-5 py-8 text-center">
            <p className="text-base font-medium text-[#111425]">No upcoming sessions scheduled</p>
            <p className="mt-2 text-sm text-[#5d647f]">Approved bookings will appear here with their Zoom links.</p>
          </div>
        ) : (
          <ul className="mt-5 space-y-3">
            {upcomingSessions.map((session) => (
              <li
                key={session.id}
                className="flex flex-col gap-4 rounded-[22px] border border-[#e3e6f2] bg-[#fbfbfe] px-4 py-4 lg:flex-row lg:items-center lg:justify-between"
              >
                <div>
                  <p className="text-sm font-semibold text-[#111425]">
                    with {session.student}
                  </p>
                  <p className="mt-1 text-sm text-[#5d647f]">
                    {formatDateTime(session.start_time)} – {new Date(session.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
                {session.zoom_join_url && (
                  <a
                    href={session.zoom_join_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg bg-[#eef0fa] px-4 py-2 text-sm font-medium text-[#282E6A] transition-colors hover:bg-[#dde0f7]"
                  >
                    Join on Zoom
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ================= Session Requests ================= */}
      <section className="rounded-[28px] border border-[#dfe4f2] bg-white/92 p-6 shadow-[0_22px_60px_-44px_rgba(23,30,67,0.35)]">
        <h2 className="text-xl font-semibold text-[#111425]">
          Session Requests
          {pendingRequests.length > 0 && (
            <span className="ml-2 rounded-full bg-amber-50 px-2.5 py-0.5 text-sm font-normal text-amber-700">
              {pendingRequests.length} pending
            </span>
          )}
        </h2>

        {pendingRequests.length === 0 ? (
          <div className="mt-5 rounded-[22px] border border-dashed border-[#d4daec] bg-[#f7f8fc] px-5 py-8 text-center">
            <p className="text-base font-medium text-[#111425]">No pending session requests</p>
            <p className="mt-2 text-sm text-[#5d647f]">New student requests will surface here as soon as they arrive.</p>
          </div>
        ) : (
          <ul className="mt-5 space-y-3">
            {pendingRequests.map((req) => (
              <li
                key={req.id}
                className="flex flex-col gap-4 rounded-[22px] border border-[#e3e6f2] bg-[#fbfbfe] px-4 py-4 lg:flex-row lg:items-center lg:justify-between"
              >
                <div>
                  <p className="text-sm font-semibold text-[#111425]">
                    from {req.student_name}
                  </p>
                  <p className="mt-1 text-sm text-[#5d647f]">
                    {formatDateTime(req.requested_start_time)} – {new Date(req.requested_end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
                <button
                  onClick={() => { setSelectedRequest(req); setRequestModalOpen(true); }}
                  className="rounded-lg bg-green-600 px-4 py-2 text-sm text-white transition-colors hover:bg-green-700"
                >
                  Review
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ================= Availability ================= */}
      <section className="rounded-[28px] border border-[#dfe4f2] bg-white/92 p-6 shadow-[0_22px_60px_-44px_rgba(23,30,67,0.35)]">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-[#111425]">Your Availability</h2>
            <p className="mt-1 text-sm text-[#5d647f]">Add clean time blocks so students can request calls without back-and-forth.</p>
          </div>
          <div className="rounded-full bg-[#eef0fa] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#282E6A]">
            {completedSessions.length} sessions completed
          </div>
        </div>

        {/* Add slot form */}
        <div className="mb-5 mt-6 flex flex-wrap items-end gap-3 rounded-[24px] border border-[#e4e8f4] bg-[#f7f8fc] p-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
              Start time
            </label>
            <DateTimePicker
              value={newSlot.start_time}
              onChange={(date) => { setNewSlot({ ...newSlot, start_time: date }); setSlotError(""); }}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
              End time
            </label>
            <DateTimePicker
              value={newSlot.end_time}
              onChange={(date) => { setNewSlot({ ...newSlot, end_time: date }); setSlotError(""); }}
            />
          </div>

          <button
            onClick={handleAdd}
            className="rounded-lg bg-[#282E6A] px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-[#1e2458]"
          >
            Add slot
          </button>
        </div>

        {slotError && (
          <p className="text-sm text-red-600 mb-3">{slotError}</p>
        )}

        {slots.length === 0 ? (
          <div className="rounded-[22px] border border-dashed border-[#d4daec] bg-[#f7f8fc] px-5 py-8 text-center">
            <p className="text-base font-medium text-[#111425]">No availability slots added yet</p>
            <p className="mt-2 text-sm text-[#5d647f]">Add your first time block to start receiving structured requests.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {slots.map((slot) => (
              <li
                key={slot.id}
                className="flex flex-col gap-3 rounded-[20px] border border-[#e3e6f2] bg-[#fbfbfe] px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <span className="text-sm text-gray-700">
                  {formatDateTime(slot.start_time)}{" "}
                  <span className="text-gray-400">–</span>{" "}
                  {new Date(slot.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
                <button
                  onClick={() => { setSelectedSlotId(slot.id); setConfirmOpen(true); }}
                  className="text-sm font-medium text-red-500 transition-colors hover:text-red-700"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default MentorDashboard;
